// src/observability.js
//
// Frontend Sentry initialization.
//
// Design decisions
// ================
// - Opt-in by env var (VITE_SENTRY_DSN). No DSN => no-op, so local dev
//   stays quiet and doesn't pollute production issue counts.
// - PII scrubbing in beforeSend: we strip the auth token from any event
//   (it lives in localStorage as `auth_token`) and redact user email and
//   secret-looking strings. An error report must never carry a token or
//   customer data to a third party.
// - Environment + release tagging so prod errors are separable from dev
//   and you can see which deploy a regression came from.
//
// Refs: https://docs.sentry.io/platforms/javascript/guides/react/

import * as Sentry from '@sentry/react';

// Secret-looking patterns to redact from any string we send.
const VALUE_PATTERNS = [
  /postgres(?:ql)?:\/\/[^\s"']+/gi,        // connection strings
  /Bearer\s+[A-Za-z0-9._-]+/gi,            // bearer tokens
  /\beyJ[A-Za-z0-9._-]+\.[A-Za-z0-9._-]+\.[A-Za-z0-9._-]+/g, // JWTs
];

const REDACTED = '[redacted]';

function scrubString(value) {
  if (typeof value !== 'string') return value;
  let out = value;
  for (const pattern of VALUE_PATTERNS) {
    out = out.replace(pattern, REDACTED);
  }
  return out;
}

function scrubDeep(obj) {
  if (obj == null) return obj;
  if (typeof obj === 'string') return scrubString(obj);
  if (Array.isArray(obj)) return obj.map(scrubDeep);
  if (typeof obj === 'object') {
    const out = {};
    for (const [key, val] of Object.entries(obj)) {
      const k = key.toLowerCase();
      if (
        k.includes('token') || k.includes('password') || k.includes('secret') ||
        k.includes('authorization') || k.includes('auth_token') ||
        k.includes('email') || k.includes('jwt') || k.includes('credential') ||
        k.includes('connection_string') || k.includes('dsn')
      ) {
        out[key] = REDACTED;
      } else {
        out[key] = scrubDeep(val);
      }
    }
    return out;
  }
  return obj;
}

export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) {
    // No DSN configured: Sentry stays off. Normal for local dev.
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_ENVIRONMENT || 'development',
    release: import.meta.env.VITE_RELEASE || undefined,
    // Browser tracing for performance; keep low for quota.
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.1,
    // Do not attach IPs or other default PII.
    sendDefaultPii: false,
    beforeSend(event) {
      try {
        // Never send the user object (could carry email/username).
        if (event.user) {
          event.user = { id: event.user.id ? '[redacted]' : undefined };
        }
        if (event.request) event.request = scrubDeep(event.request);
        if (event.extra) event.extra = scrubDeep(event.extra);
        if (event.contexts) event.contexts = scrubDeep(event.contexts);
        if (event.breadcrumbs) {
          event.breadcrumbs = event.breadcrumbs.map((b) => ({
            ...b,
            message: scrubString(b.message),
            data: scrubDeep(b.data),
          }));
        }
        if (event.exception?.values) {
          event.exception.values = event.exception.values.map((v) => ({
            ...v,
            value: scrubString(v.value),
          }));
        }
      } catch {
        // If scrubbing fails, drop the event rather than risk a leak.
        return null;
      }
      return event;
    },
  });
}